import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  Alert,
  CircularProgress,
  Grid,
  Card,
  CardContent,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
} from '@mui/material';
import {
  Edit,
  Save,
  Cancel,
  Person,
  Email,
  LocationOn,
  Payment,
  CalendarToday,
  Close,
} from '@mui/icons-material';
import { Auth } from 'aws-amplify';
import { useAuth } from '../contexts/AuthContext';
import { userAPI, UserProfile } from '../services/apiService';

interface SettingsModalProps {
  open: boolean;
  onClose: () => void;
}

const US_STATES = [
  'Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California', 'Colorado', 'Connecticut',
  'Delaware', 'Florida', 'Georgia', 'Hawaii', 'Idaho', 'Illinois', 'Indiana', 'Iowa',
  'Kansas', 'Kentucky', 'Louisiana', 'Maine', 'Maryland', 'Massachusetts', 'Michigan',
  'Minnesota', 'Mississippi', 'Missouri', 'Montana', 'Nebraska', 'Nevada', 'New Hampshire',
  'New Jersey', 'New Mexico', 'New York', 'North Carolina', 'North Dakota', 'Ohio',
  'Oklahoma', 'Oregon', 'Pennsylvania', 'Rhode Island', 'South Carolina', 'South Dakota',
  'Tennessee', 'Texas', 'Utah', 'Vermont', 'Virginia', 'Washington', 'West Virginia',
  'Wisconsin', 'Wyoming'
];

const SettingsModal: React.FC<SettingsModalProps> = ({ open, onClose }) => {
  const { user, updateUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  
  const [editingName, setEditingName] = useState(false);
  const [editingState, setEditingState] = useState(false);
  const [name, setName] = useState(user?.name || '');
  const [employmentState, setEmploymentState] = useState(user?.employmentState || '');

  const loadUserProfile = async () => {
    try {
      setLoading(true);
      const profile = await userAPI.getProfile();
      setUserProfile(profile);
      setName(profile.name);
      setEmploymentState(profile.employmentState);
    } catch (err: any) {
      setError('Failed to load profile. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open && user && !loading) {
      loadUserProfile();
    }
  }, [open]);

  useEffect(() => {
    if (userProfile) {
      setName(userProfile.name);
      setEmploymentState(userProfile.employmentState);
    }
  }, [userProfile]);

  const handleSaveName = async () => {
    if (!name.trim()) {
      setError('Name cannot be empty');
      return;
    }

    try {
      setLoading(true);
      setError('');
      await userAPI.updateProfile({ name: name.trim() });
      
      const updatedProfile = { ...userProfile!, name: name.trim() };
      setUserProfile(updatedProfile);
      updateUser({ name: name.trim() });
      
      setEditingName(false);
      setSuccess('Name updated successfully');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      setError('Failed to update name. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveState = async () => {
    if (!employmentState) {
      setError('Employment state cannot be empty');
      return;
    }

    try {
      setLoading(true);
      setError('');
      await userAPI.updateProfile({ employmentState });
      
      const updatedProfile = { ...userProfile!, employmentState };
      setUserProfile(updatedProfile);
      updateUser({ employmentState });
      
      setEditingState(false);
      setSuccess('Employment state updated successfully');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      setError('Failed to update employment state. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelNameEdit = () => {
    setName(userProfile?.name || '');
    setEditingName(false);
    setError('');
  };

  const handleCancelStateEdit = () => {
    setEmploymentState(userProfile?.employmentState || '');
    setEditingState(false);
    setError('');
  };

  const handleLogout = async () => {
    try {
      await Auth.signOut();
      onClose();
      window.location.href = '/login';
    } catch (err) {
      setError('Failed to log out. Please try again.');
    }
  };

  const formatServiceType = (serviceType: string) => {
    return serviceType === 'fmla' ? 'FMLA Certification' : 'FMLA + STD Certification';
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          Account Settings
          <IconButton onClick={onClose}>
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>
      
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}
        
        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
            {success}
          </Alert>
        )}

        <Grid container spacing={3}>
          {/* Personal Information */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Personal Information
                </Typography>
                
                {/* Name Field */}
                <Box sx={{ mb: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <Person sx={{ mr: 1, color: 'text.secondary' }} />
                    <Typography variant="subtitle2" color="text.secondary">
                      Full Name
                    </Typography>
                  </Box>
                  {editingName ? (
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                      <TextField
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        size="small"
                        fullWidth
                        disabled={loading}
                      />
                      <IconButton onClick={handleSaveName} disabled={loading}>
                        <Save />
                      </IconButton>
                      <IconButton onClick={handleCancelNameEdit} disabled={loading}>
                        <Cancel />
                      </IconButton>
                    </Box>
                  ) : (
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <Typography variant="body1">
                        {userProfile?.name || 'Not provided'}
                      </Typography>
                      <IconButton onClick={() => setEditingName(true)} size="small">
                        <Edit />
                      </IconButton>
                    </Box>
                  )}
                </Box>

                {/* Email Field (Read-only) */}
                <Box sx={{ mb: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <Email sx={{ mr: 1, color: 'text.secondary' }} />
                    <Typography variant="subtitle2" color="text.secondary">
                      Email Address
                    </Typography>
                  </Box>
                  <Typography variant="body1">
                    {userProfile?.email || user?.email}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Email cannot be changed
                  </Typography>
                </Box>

                {/* Employment State Field */}
                <Box sx={{ mb: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <LocationOn sx={{ mr: 1, color: 'text.secondary' }} />
                    <Typography variant="subtitle2" color="text.secondary">
                      Employment State
                    </Typography>
                  </Box>
                  {editingState ? (
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                      <FormControl size="small" fullWidth>
                        <Select
                          value={employmentState}
                          onChange={(e) => setEmploymentState(e.target.value)}
                          disabled={loading}
                        >
                          {US_STATES.map((state) => (
                            <MenuItem key={state} value={state}>
                              {state}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      <IconButton onClick={handleSaveState} disabled={loading}>
                        <Save />
                      </IconButton>
                      <IconButton onClick={handleCancelStateEdit} disabled={loading}>
                        <Cancel />
                      </IconButton>
                    </Box>
                  ) : (
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <Typography variant="body1">
                        {userProfile?.employmentState || 'Not specified'}
                      </Typography>
                      <IconButton onClick={() => setEditingState(true)} size="small">
                        <Edit />
                      </IconButton>
                    </Box>
                  )}
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Service Information */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Service Information
                </Typography>
                
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Payment sx={{ mr: 1, color: 'text.secondary' }} />
                      <Box>
                        <Typography variant="subtitle2" color="text.secondary">
                          Service Type
                        </Typography>
                        <Typography variant="body1">
                          {userProfile?.serviceType ? formatServiceType(userProfile.serviceType) : 'Not specified'}
                        </Typography>
                        <Chip 
                          label={`$${userProfile?.paymentAmount || 100}`} 
                          color="primary" 
                          size="small"
                          sx={{ mt: 0.5 }}
                        />
                      </Box>
                    </Box>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
            <CircularProgress />
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleLogout} color="error">
          Log Out
        </Button>
        <Button onClick={onClose} variant="contained">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SettingsModal;